import { FC, useCallback } from "react";
import { FormControlLabel, Stack, Switch, Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAppDispatch } from "@/store/hooks";
import { ChartSettings } from "@/services/cloudchipr.api";
import { updateBillingExplorerChartSettingsThunk } from "@/store/resource-explorer/thunks/resource-explorer/updateBillingExplorerChartSettingsThunk";

interface BillingExplorerChartSettingSwitcherProps {
  type: Exclude<
    keyof ChartSettings,
    | "scale"
    | "series"
    | "show_budget_line"
    | "selected_budget_id"
    | "chart_type"
    | "chart_base_type"
  >;
  checked: boolean;
  onChange?(checked: boolean): void;
}

export const BillingExplorerChartSettingSwitcher: FC<
  BillingExplorerChartSettingSwitcherProps
> = ({ checked, type, onChange }) => {
  const dispatch = useAppDispatch();

  const changeHandler = useCallback(() => {
    dispatch(updateBillingExplorerChartSettingsThunk({ [type]: !checked }));
    onChange?.(!checked);
  }, [dispatch, type, onChange, checked]);

  const tooltip = tooltipByType[type];

  return (
    <Stack pl={1} mt={1.5} direction="row" alignItems="center">
      <FormControlLabel
        onChange={changeHandler}
        checked={checked}
        sx={{ width: "fit-content" }}
        control={<Switch size="small" />}
        label={<Typography variant="body2">{titleByType[type]}</Typography>}
      />

      {tooltip && (
        <Tooltip arrow placement="top" title={tooltip}>
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      )}
    </Stack>
  );
};

const titleByType = {
  show_data_labels: "Show Data Labels",
  show_legends: "Show Legends",
  show_trendline: "Show TrendLine",
  cumulative_line: "Cumulative Line",
  display_forecast: "Display Forecast",
};

const tooltipByType: Partial<
  Record<BillingExplorerChartSettingSwitcherProps["type"], string>
> = {
  cumulative_line: "Show running totals over time.",
  display_forecast:
    "Displays forecast data as bars on the chart. Forecasts are only shown when the selected date range is Month-to-Date (MTD) or Quarter-to-Date (QTD) and Date Granularity is Daily.",
};
